import { ResponseContext } from './types';
import { knowledgeBase } from './knowledgeBase';

function getMetricStatus(value: number, metric: 'recovery' | 'strain' | 'sleep'): 'low' | 'moderate' | 'high' {
  switch (metric) {
    case 'recovery':
      return value < 33 ? 'low' : value < 66 ? 'moderate' : 'high';
    case 'strain':
      return value < 8 ? 'low' : value < 14 ? 'moderate' : 'high';
    case 'sleep':
      return value < 70 ? 'low' : value < 85 ? 'moderate' : 'high';
  }
}

function generateContextualAdvice(context: ResponseContext): string {
  const { metrics, currentQuery } = context;
  let response = '';

  const query = currentQuery.toLowerCase();

  // Handle nutrition questions
  if (query.includes('nutrition') || query.includes('diet') || query.includes('meal')) {
    const { nutrition } = knowledgeBase;
    response = `Based on your activity level and metrics, here's your nutrition plan:\n\n`;
    
    if (metrics.strain.current > 14) {
      const postWorkout = nutrition.mealSuggestions.breakfast[1]; // Muscle Builder
      response += `Post-workout meal suggestion:\n`;
      response += `• ${postWorkout.name}\n`;
      response += `• Macros: ${Object.entries(postWorkout.macros).map(([key, value]) => `${key}: ${value}`).join(', ')}\n\n`;
    }

    response += "Daily nutrition targets:\n";
    response += nutrition.recommendations.mealPlanning.map(rec => `• ${rec}`).join('\n');
    return response;
  }

  // Handle mindfulness/breathing questions
  if (query.includes('breath') || query.includes('mindful') || query.includes('meditation')) {
    const { mindfulness } = knowledgeBase;
    const exercise = mindfulness.breathing.exercises[0];
    response = `Let's practice the ${exercise.name} technique:\n\n`;
    response += exercise.steps.map((step, i) => `${i + 1}. ${step}`).join('\n');
    response += '\n\nBenefits:\n';
    response += exercise.benefits.map(benefit => `• ${benefit}`).join('\n');
    return response;
  }

  // Handle workout recommendations
  if (query.includes('workout')) {
    const recoveryStatus = getMetricStatus(metrics.recovery, 'recovery');
    response = `Based on your ${metrics.recovery}% recovery, here's what I recommend:\n\n`;
    response += knowledgeBase.recovery.recommendations[recoveryStatus].map(rec => `• ${rec}`).join('\n');
    return response;
  }

  // Handle recovery questions
  if (query.includes('recovery')) {
    response = `Your current recovery is at ${metrics.recovery}%. `;
    response += `HRV is ${metrics.hrv.current}ms (${metrics.hrv.current > metrics.hrv.yesterday ? 'up' : 'down'} from yesterday). `;
    response += `\n\nRecommendations:\n`;
    response += knowledgeBase.recovery.recommendations[getMetricStatus(metrics.recovery, 'recovery')]
      .map(rec => `• ${rec}`).join('\n');
    return response;
  }

  // Default response
  response = `I see your metrics are:\n`;
  response += `• Recovery: ${metrics.recovery}%\n`;
  response += `• Readiness: ${metrics.readiness}%\n`;
  response += `• Current Strain: ${metrics.strain.current.toFixed(1)}\n\n`;
  response += `How can I help you optimize your performance today?`;

  return response;
}

export function generateResponse(context: ResponseContext): string {
  try {
    return generateContextualAdvice(context);
  } catch (error) {
    console.error('Error generating response:', error);
    return "I apologize, but I encountered an error. Please try again.";
  }
}